"use client"
import {
  Box,
  Button,
  Flex,
  Text,
  Image,
  Textarea,
  Spinner,
  Spacer,
  BoxProps,
  useToast,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Grid,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useGetCustomPromptMaxLength } from "queries";
import { IoMdClose } from "react-icons/io";
import { Lora, Model } from "types";
import LoraSelector from "./Generator/LoraSelector";
import { useAppContext } from "hooks";
import {AsyncDisplay, CustomRatioModal} from "components"


const FluxPlayground = ({
  prompt,
  setPrompt,
  handleGenerateImage,
  generatedData,
  isGenerating,
  error,
  mt = { base: "0", md: "0" },
  /* refetchTrigger, */
  constantLora,
  handleConstantLoraChange, 
  model, 
}: {
  prompt: string;
  setPrompt: (value: string) => void;
  handleGenerateImage: (loras: Lora[], ratio: string,  numGenerations: number) => void;
  generatedData?: { image_urls: string[] };
  isGenerating: boolean;
  error?: Error | null;
  mt?: BoxProps['mt']; 
  refetchTrigger?: () => void;
  constantLora: Lora;
  handleConstantLoraChange: (newScale: number) => void; // Add th
  model: Model | undefined; // Add the type for `model`
}) => {

  const toast = useToast();

  interface Lora {
    path: string;
    scale: number;
  }
  const { data: customPromptMaxLength } = useGetCustomPromptMaxLength();
  const [selectedLoraImage, setSelectedLoraImage] = useState<string>('');
  const [selectedLoraTitle, setSelectedLoraTitle] = useState<string>('');
  const [dynamicLoras, setDynamicLoras] = useState<Lora[]>([]);
  const [selectedRatio, setSelectedRatio] = useState<string>('portrait_16_9');
  const [numGenerations, setNumGenerations] = useState<number>(1); 
  const [activeGenerations, setActiveGenerations] = useState<number>(0); // Track active requests
  const [activeRequests, setActiveRequests] = useState<number>(0); // ✅ Track active async requests
  const { profile } = useAppContext();
  const isInsufficientCredits = (profile?.credit ?? 0) < numGenerations;

  const handleAddLora = (
    triggerWords: string, 
    loraData?: { 
      path: string, 
      scale: number,
      image?: string,
      title?: string,
      default_scale?: number
    }
  ) => {
    console.log('Lora Data:', loraData);
    if (dynamicLoras.length > 0) {
      toast({
        title: "Only one Style allowed",
        description: "You can only add one optional style.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
  
    const newPrompt = prompt.trim() + (prompt.trim() ? ', ' : '') + triggerWords;
    setPrompt(newPrompt);
  
    if (loraData) {
      setDynamicLoras([{
        path: loraData.path,
        scale: loraData.scale,
      }]);
      
      if (loraData.image) setSelectedLoraImage(loraData.image);
      if (loraData.title) setSelectedLoraTitle(loraData.title);
    }
  };

  const handleRemoveLora = (index: number) => {
    setDynamicLoras(dynamicLoras.filter((_, i) => i !== index));
  };

  const handleUpdateLora = (index: number, key: keyof Lora, value: Lora[keyof Lora]) => {
    const updatedLoras = [...dynamicLoras];
    
    (updatedLoras[index][key as keyof Lora] as Lora[keyof Lora]) = value;
  
    setDynamicLoras(updatedLoras);
  };
  

 /* useEffect(() => {
    if (generatedData && refetchTrigger) {
      refetchTrigger();
    }
  }, [generatedData, refetchTrigger]); */

  const [isCustomModalOpen, setIsCustomModalOpen] = useState(false);
  const [customSize, setCustomSize] = useState<{ width: number; height: number } | null>(null);

  const computedSize =
    selectedRatio === "custom" && customSize
      ? `${customSize.width}x${customSize.height}`
      : selectedRatio;

    
  const onGenerateClick = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Error",
        description: "Prompt cannot be empty.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if ((profile?.credit ?? 0) < numGenerations) {
      toast({
        title: "Insufficient Credits",
        description: `You need at least ${numGenerations} credit${numGenerations > 1 ? 's' : ''} to generate ${numGenerations} image${numGenerations > 1 ? 's' : ''}.`,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    
  
  
    setActiveRequests((prev) => prev + numGenerations); // ✅ Increment only when sending a request
    handleGenerateImage(dynamicLoras, computedSize, numGenerations);
  };


  return (
    <Grid
    templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
    w="100%"
    maxW="2420px"
    bg="transparent"
    borderRadius="12px"
    p={{ base: "2px", lg: "16px" }}
    gap={{ base: "12px", lg: "23px" }}
    mt={mt}
    alignItems="start"
  >
    <Flex
      direction="column"
      bg="#000000"
      borderRadius="20px"
      p="32px"
      border="1px solid rgba(255, 255, 255, 0.5)"
      gap={4}
        height="100%"
    >
              <Text fontWeight={600} color="white" mb={1}>
                Describe what you <span style={{ color: "#FF1B6D" }}>want</span>{" "}
                to see in the image.
              </Text>
              
            <Box position="relative">

            <Textarea
            placeholder="For example: realistic photo of a woman with white shirt, on the beach, winter day, looking at the camera"
            size="md"
            bg="black"
            color="white"
            border="1px solid #818181"
            borderRadius="20px"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            _placeholder={{ color: "gray", textAlign: "left", paddingTop: "5px" }}
            textAlign="left"
            lineHeight="1.5"
            w="100%"
            h={{ base: "220px", md: "300px" }}
            mt={4}
            resize="none"
            maxLength={1000}
          />
             <Text position="absolute" color="gray" bottom={2} right={2}>{`${
                prompt.length
              }/${customPromptMaxLength?.max_length ?? 0}`}</Text>
         </Box>
         <Box
         mt={4}
          p={4}
          borderRadius="20px"
          border="1px solid #818181"
          bg="rgba(0, 0, 0, 0.3)"
          w="100%"
        >
          <Flex w="100%" align="center" gap={4}>
            <Image
              src={model?.picture || ""}
              alt="Model"
              boxSize="60px"
              objectFit="cover"
              borderRadius="8px"
            />
            <Box flex="1">
              <Text color="white" mb={2} fontWeight="medium">
                Model: {model?.display_name}
              </Text>
              <Flex align="center" gap={2}>
                <Text color="gray.300" fontSize="sm" minWidth="45px">
                  Scale:
                </Text>
                <Slider
                  aria-label="model-scale"
                  defaultValue={1.2}
                  min={0.5}
                  max={2.0}
                  step={0.1}
                  value={constantLora.scale}
                  onChange={(value) => {
                    const updatedLora = { ...constantLora, scale: value };
                    handleConstantLoraChange(updatedLora.scale);
                  }}
                  flex="1"
                >
                  <SliderTrack bg="gray.600">
                    <SliderFilledTrack bg="#FF1B6D" />
                  </SliderTrack>
                  <SliderThumb boxSize={3} bg="white" />
                </Slider>
                <Text color="gray.300" fontSize="sm" minWidth="30px">
                  {constantLora.scale.toFixed(1)}
                </Text>
              </Flex>
            </Box>
          </Flex>
        </Box>
         {dynamicLoras.length > 0 && (
            <Box
            w="100%"
            mt={4}
            p={4}
            borderRadius="20px"
            border="1px solid rgba(255, 255, 255, 0.5)"
            bg="rgba(0, 0, 0, 0.3)"
            position="relative" // ADD THIS
          >
            {/* X button */}
            <Button
              size="xs"
              variant="ghost"
              color="gray.400"
              _hover={{ color: "white" }}
              onClick={() => {
                setDynamicLoras([]);
                setPrompt("");
              }}
              position="absolute"
              top="2"
              right="2"
              zIndex="1"
            >
              <IoMdClose size="18px" />
            </Button>

            <Flex w="100%" align="center" gap={4}>
              <Image
                src={selectedLoraImage}
                alt="Selected Lora"
                boxSize="60px"
                objectFit="cover"
                borderRadius="8px"
              />
              <Box flex="1">
                <Text color="white" mb={2} fontWeight="medium">
                  Style: {selectedLoraTitle}
                </Text>
                <Flex align="center" gap={2}>
                  <Text color="gray.300" fontSize="sm" minWidth="45px">
                    Scale:
                  </Text>
                  <Slider
                    aria-label="lora-scale"
                    value={dynamicLoras[0]?.scale ?? 1.2}
                    min={0}
                    max={2}
                    step={0.1}
                    onChange={(value) => {
                      const updatedLoras = [...dynamicLoras];
                      updatedLoras[0].scale = value;
                      setDynamicLoras(updatedLoras);
                    }}
                    flex="1"
                  >
                    <SliderTrack bg="gray.600">
                      <SliderFilledTrack bg="#FF1B6D" />
                    </SliderTrack>
                    <SliderThumb boxSize={3} bg="white" />
                  </Slider>
                  <Text color="gray.300" fontSize="sm" minWidth="30px">
                    {dynamicLoras[0]?.scale.toFixed(1)}
                  </Text>
                </Flex>
              </Box>
            </Flex>
          </Box>
          )}
         <LoraSelector isFlux={true} onSelect={handleAddLora} />
         <Box ml={4}>

            <Text color="#D5D5D5" fontSize="14px" fontWeight="bold" mb={4}>
              Ratio
            </Text>
            <Flex gap={3} flexWrap="wrap">
              {[
                { key: "portrait_16_9", label: "Portrait" },
                { key: "square_hd", label: "Square" },
                { key: "landscape_16_9", label: "Landscape" },
              ].map((option) => (
                <Button
                  key={option.key}
                  size="sm"
                  bg={
                    selectedRatio === option.key
                      ? "linear-gradient(to right, #FFB5FF, #1AA5D3)"
                      : "transparent"
                  }
                  color={selectedRatio === option.key ? "black" : "white"}
                  _hover={{
                    bg:
                      selectedRatio === option.key
                        ? "linear-gradient(to right, #FFE1FF, #1AA5D3)"
                        : "rgba(255, 255, 255, 0.1)",
                  }}
                  borderRadius="full"
                  border={
                    selectedRatio === option.key
                      ? "none"
                      : "1px solid rgba(255, 255, 255, 0.5)"
                  }
                  onClick={() => setSelectedRatio(option.key)}
                >
                  {option.label}
                </Button>
              ))}
              <Button
                size="sm"
                onClick={() => setIsCustomModalOpen(true)}
                bg={
                  selectedRatio === "custom"
                    ? "linear-gradient(to right, #FFB5FF, #1AA5D3)"
                    : "transparent"
                }
                color={selectedRatio === "custom" ? "black" : "white"}
                _hover={{
                  bg:
                    selectedRatio === "custom"
                      ? "linear-gradient(to right, #FFE1FF, #1AA5D3)"
                      : "rgba(255, 255, 255, 0.1)",
                }}
                borderRadius="full"
                border={
                  selectedRatio === "custom"
                    ? "none"
                    : "1px solid rgba(255, 255, 255, 0.5)"
                }
              >
                Custom
              </Button>
            </Flex>
            {/* Number of Generations Selector */}
        <Box mt={4}>
          <Text color="#D5D5D5" fontSize="14px" fontWeight="bold" mb={4}>
            Number of Generations
          </Text>
          <Flex gap={3}>
            {[1, 2, 3, 4].map((count) => (
              <Button
                key={count}
                size="sm"
                bg={numGenerations === count ? "linear-gradient(to right, #FFB5FF, #1AA5D3)" : "transparent"}
                color={numGenerations === count ? "black" : "white"}
                _hover={{
                  bg:
                    numGenerations === count
                      ? "linear-gradient(to right, #FFE1FF, #1AA5D3)"
                      : "rgba(255, 255, 255, 0.1)",
                }}
                borderRadius="full"
                border={
                  numGenerations === count
                    ? "none"
                    : "1px solid rgba(255, 255, 255, 0.5)"
                }
                onClick={() => setNumGenerations(count)}
              >
                {count}
              </Button>
            ))}
          </Flex>
        </Box>

          </Box>          
          <Button
  w="100%"
  h="50px"
  bg={isInsufficientCredits ? "gray.600" : "#FF1B6D"}
  color="white"
  borderRadius="6px"
  fontWeight="bold"
  onClick={onGenerateClick}
  isDisabled={isInsufficientCredits}
  outline="none"
  _focus={{ outline: "none", boxShadow: "none" }}
  mt={12}
>
  {isInsufficientCredits
    ? "Insufficient Credits"
    : `Create (${numGenerations} credit${numGenerations > 1 ? "s" : ""})`}
</Button>

            {error && <Text color="red.500">Error: {error.message}</Text>}
              </Flex>
              <CustomRatioModal
                isOpen={isCustomModalOpen}
                onClose={() => {
                  setIsCustomModalOpen(false);
                  if (selectedRatio === "custom" && !customSize) {
                    setSelectedRatio("portrait_16_9");
                  }
                }}
                onSave={(width, height) => {
                  setCustomSize({ width, height });
                  setSelectedRatio("custom");
                  setIsCustomModalOpen(false);
                }}
              />
        {/* Right Box - Image Display */}
       <Flex
          bg="#000000"
          h="100%"
          w="100%"
          borderRadius="20px"
          alignItems="center"
          justifyContent="center"
          p={4}
          border="1px solid rgba(255, 255, 255, 0.5)"
          boxShadow="0px 0px 0px 2px linear-gradient(to right, #FFE1FF 30%, #A4DBEE 100%)"
        >
          {isGenerating ? (
            <AsyncDisplay modelId={model?.id || ""} isGenerating={activeRequests > 0} generatedData={generatedData}   numGenerations={numGenerations}  activeRequests={activeRequests} // Pass down activeRequests
            setActiveRequests={setActiveRequests} />
          ) : (
            <AsyncDisplay modelId={model?.id || ""} isGenerating={activeRequests > 0} generatedData={generatedData}   numGenerations={numGenerations}  activeRequests={activeRequests} // Pass down activeRequests
            setActiveRequests={setActiveRequests} />
          )}
        </Flex>
      </Grid>
  );
};

export default FluxPlayground;
